import axios from 'axios';
import { auth } from '@/firebase';
import { getFunctionUrl } from '@/functions/functionsConfig';
import store from '@/store';
import getWorkspaceIdForProjectQuery from '@/utils/apiUtils.js';

const manageScreenersEndpoint = getFunctionUrl('manage_screeners');

function resolveWorkspaceId(projectId) {
  if (projectId) {
    return getWorkspaceIdForProjectQuery(projectId);
  }
  return store.state.userWorkspace.id;
}

/**
 * Create a screener (opt-in form).
 * @param {Object} options
 * @param {string} options.name
 * @param {string} options.projectId
 * @param {'automatic'|'manual'} options.qualifyMethod
 * @param {'screener'|'opt_in'} [options.type]
 */
const createScreener = async ({
  name,
  projectId,
  qualifyMethod,
  type = 'opt_in',
} = {}) => {
  try {
    const requestBody = {
      user_id: auth.currentUser.uid,
      workspace_id: resolveWorkspaceId(projectId),
      name,
      project_id: projectId,
      qualify_method: qualifyMethod,
      type,
    };

    const response = await axios.post(
      `${manageScreenersEndpoint}/create`,
      requestBody,
    );

    const payload = response.data?.data ?? response.data;
    const status = response.data?.status || 'success';

    if (status === 'error') {
      return {
        status: 'error',
        error: response.data?.error || response.data?.message || response.data || payload,
      };
    }

    return { status: 'success', data: payload };
  } catch (error) {
    return { status: 'error', error };
  }
};

/**
 * Update screener name.
 * @param {Object} options
 * @param {string} options.screenerId
 * @param {string} options.name
 * @param {string} [options.projectId] - Used only to resolve workspace_id
 */
const updateScreener = async ({
  screenerId,
  name,
  projectId = null,
} = {}) => {
  try {
    const requestBody = {
      user_id: auth.currentUser.uid,
      workspace_id: resolveWorkspaceId(projectId),
      screener_id: screenerId,
      name,
    };

    const response = await axios.put(
      `${manageScreenersEndpoint}/update`,
      requestBody,
    );

    const payload = response.data?.data ?? response.data;
    const status = response.data?.status || 'success';

    if (status === 'error') {
      return {
        status: 'error',
        error:
          response.data?.error ||
          response.data?.message ||
          response.data ||
          payload,
      };
    }

    return { status: 'success', data: payload };
  } catch (error) {
    return { status: 'error', error };
  }
};

/**
 * List screeners for a workspace (org-level) or project.
 * @param {Object} options
 * @param {string} [options.projectId]
 * @param {number} [options.page=1]
 * @param {number} [options.pageSize=20]
 * @param {'screener'|'opt_in'} [options.type]
 * @param {string} [options.search]
 * @param {string} [options.qualifyMethod]
 * @param {string} [options.status]
 * @param {string} [options.filterProjectId] - Project filter (opt-in list); distinct from scope projectId
 */
const listScreeners = async ({
  projectId = null,
  page = 1,
  pageSize = 20,
  type = null,
  search = '',
  qualifyMethod = '',
  status = '',
  filterProjectId = null,
} = {}) => {
  try {
    const scopedProjectId = projectId || null;
    const params = {
      user_id: auth.currentUser.uid,
      workspace_id: resolveWorkspaceId(scopedProjectId),
      page,
      page_size: pageSize,
    };

    const effectiveProjectId = scopedProjectId || filterProjectId || null;
    if (effectiveProjectId) {
      params.project_id = effectiveProjectId;
    }

    const resolvedType = type || (scopedProjectId ? 'screener' : 'opt_in');
    if (resolvedType) {
      params.type = resolvedType;
    }

    const trimmedSearch = typeof search === 'string' ? search.trim() : '';
    if (trimmedSearch) {
      params.search = trimmedSearch;
    }

    if (qualifyMethod) {
      params.qualify_method = qualifyMethod;
    }

    if (status) {
      params.status = status;
    }

    const response = await axios.get(`${manageScreenersEndpoint}/list`, {
      params,
    });

    const payload = response.data?.data ?? response.data;
    const responseStatus = response.data?.status || 'success';

    if (responseStatus === 'error') {
      return { status: 'error', error: response.data?.error || payload };
    }

    return { status: 'success', data: payload };
  } catch (error) {
    return { status: 'error', error };
  }
};

/**
 * Update screener questions (draft or publish).
 * @param {Object} options
 * @param {string} options.screenerId
 * @param {'draft'|'published'} options.status
 * @param {Array} options.questions
 * @param {string} [options.projectId]
 */
const updateScreenerQuestions = async ({
  screenerId,
  status,
  questions = [],
  projectId = null,
} = {}) => {
  try {
    const requestBody = {
      user_id: auth.currentUser.uid,
      workspace_id: resolveWorkspaceId(projectId),
      screener_id: screenerId,
      status,
      questions,
    };

    if (projectId) {
      requestBody.project_id = projectId;
    }

    const response = await axios.put(
      `${manageScreenersEndpoint}/update-questions`,
      requestBody,
    );

    const payload = response.data?.data ?? response.data;
    const responseStatus = response.data?.status || 'success';

    if (responseStatus === 'error') {
      return {
        status: 'error',
        error:
          response.data?.error ||
          response.data?.message ||
          response.data ||
          payload,
      };
    }

    return { status: 'success', data: payload };
  } catch (error) {
    return { status: 'error', error };
  }
};

/**
 * Get screener detail by id.
 * @param {Object} options
 * @param {string} options.screenerId
 * @param {string} [options.projectId]
 */
const getScreenerDetail = async ({ screenerId, projectId = null } = {}) => {
  try {
    const params = {
      user_id: auth.currentUser.uid,
      workspace_id: resolveWorkspaceId(projectId),
      screener_id: screenerId,
    };

    if (projectId) {
      params.project_id = projectId;
    }

    const response = await axios.get(`${manageScreenersEndpoint}/detail`, {
      params,
    });

    const payload = response.data?.data ?? response.data;
    const status = response.data?.status || 'success';

    if (status === 'error') {
      return {
        status: 'error',
        error:
          response.data?.error ||
          response.data?.message ||
          response.data ||
          payload,
      };
    }

    return { status: 'success', data: payload };
  } catch (error) {
    return { status: 'error', error };
  }
};

export default {
  createScreener,
  updateScreener,
  listScreeners,
  getScreenerDetail,
  updateScreenerQuestions,
};
